import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import dayjs, { type Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '../AxiosInstance';
import ConfirmDialog from './shared/ConfirmDialog';

export const NOTE_MAX = 280;
export const NOTE_RULES_TOOLTIP = `Letters, numbers, spaces, and . , ! ? ' - ( ) : ; only. Max ${NOTE_MAX} characters.`;
const NOTE_CHAR = /^[a-zA-Z0-9 .,!?'\-():;]$/;

/** One-third row width; remaining space stays empty (3-column mental model). */
const remindDatePickerRowSx: SxProps<Theme> = { display: 'flex', width: '100%' };
const remindDatePickerColSx: SxProps<Theme> = {
    flex: '0 0 33.333%',
    maxWidth: '33.333%',
    minWidth: 0,
};

export function filterNoteInput(value: string): string {
    return value.split('').filter((ch) => NOTE_CHAR.test(ch)).join('');
}

export function toNotifyUtcIso(dayjsValue: Dayjs | null): string | null {
    if (!dayjsValue || !dayjsValue.isValid()) return null;
    return `${dayjsValue.format('YYYY-MM-DD')}T00:00:00.000Z`;
}

export function fromNotifyUtc(iso: string | null | undefined): Dayjs | null {
    if (!iso) return null;
    const utcDate = new Date(iso);
    if (Number.isNaN(utcDate.getTime())) return null;

    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');

    return dayjs(`${year}-${month}-${day}`);
}

export type CustomerNoteDto = {
    customerNoteId: number;
    customerId: number;
    text: string;
    createdUtc: string;
    updatedUtc: string;
    createdBy: string;
    updatedBy: string;
    notifyDateTimeUtc?: string | null;
};

export type CustomerNotesPanelProps = {
    customerId: number;
    customerName?: string | null;
    onNotesChanged?: () => void;
};

const CustomerNotesPanel: React.FC<CustomerNotesPanelProps> = ({
    customerId,
    customerName,
    onNotesChanged,
}) => {
    const [notes, setNotes] = useState<CustomerNoteDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newText, setNewText] = useState('');
    const [newReminder, setNewReminder] = useState<Dayjs | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [editReminder, setEditReminder] = useState<Dayjs | null>(null);
    const [noteIdPendingDelete, setNoteIdPendingDelete] = useState<number | null>(null);

    const fetchNotes = useCallback(async () => {
        if (!customerId || customerId < 1) {
            setNotes([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await axiosInstance.get<CustomerNoteDto[]>(`/customers/${customerId}/notes`);
            setNotes(response.data || []);
        } catch (e) {
            console.error('Failed to load customer notes', e);
            setNotes([]);
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        void fetchNotes();
    }, [fetchNotes]);

    const handleAdd = async () => {
        const trimmed = newText.trim();
        if (!trimmed) return;
        setSaving(true);
        try {
            await axiosInstance.post(`/customers/${customerId}/notes`, {
                text: trimmed,
                notifyDateTimeUtc: toNotifyUtcIso(newReminder),
            });
            setNewText('');
            setNewReminder(null);
            await fetchNotes();
            onNotesChanged?.();
            if (window.refreshNotifications) window.refreshNotifications();
        } catch (e: unknown) {
            const err = e as { response?: { data?: unknown }; message?: string };
            const msg = err.response?.data ?? err.message ?? 'Could not add note';
            window.alert(typeof msg === 'string' ? msg : 'Could not add note');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (note: CustomerNoteDto) => {
        setEditId(note.customerNoteId);
        setEditText(note.text);
        setEditReminder(fromNotifyUtc(note.notifyDateTimeUtc));
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditText('');
        setEditReminder(null);
    };

    const saveEdit = async () => {
        const trimmed = editText.trim();
        if (!trimmed || editId == null) return;
        setSaving(true);
        try {
            await axiosInstance.put(`/customers/${customerId}/notes/${editId}`, {
                text: trimmed,
                notifyDateTimeUtc: toNotifyUtcIso(editReminder),
            });
            cancelEdit();
            await fetchNotes();
            onNotesChanged?.();
            if (window.refreshNotifications) window.refreshNotifications();
        } catch (e: unknown) {
            const err = e as { response?: { data?: unknown }; message?: string };
            const msg = err.response?.data ?? err.message ?? 'Could not update note';
            window.alert(typeof msg === 'string' ? msg : 'Could not update note');
        } finally {
            setSaving(false);
        }
    };

    const closeDeleteDialog = () => setNoteIdPendingDelete(null);

    const confirmDeleteNote = async () => {
        const noteId = noteIdPendingDelete;
        if (noteId == null) return;
        setNoteIdPendingDelete(null);
        setSaving(true);
        try {
            await axiosInstance.delete(`/customers/${customerId}/notes/${noteId}`);
            await fetchNotes();
            onNotesChanged?.();
            if (window.refreshNotifications) window.refreshNotifications();
        } catch (e) {
            console.error(e);
            window.alert('Could not delete note');
        } finally {
            setSaving(false);
        }
    };

    const formatStamp = (iso: string) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    /** Reminder is stored as UTC DateTime in the API; UI treats it as a calendar date only for now. */
    const formatReminderDate = (iso: string) => {
        const reminderDate = fromNotifyUtc(iso);
        return reminderDate?.isValid() ? reminderDate.format('MMM D, YYYY') : iso;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Notes{customerName ? ` — ${customerName}` : ''}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto', mb: 2 }}>
                        {notes.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No notes yet.
                            </Typography>
                        ) : (
                            notes.map((note) => (
                                <React.Fragment key={note.customerNoteId}>
                                    <ListItem
                                        alignItems="flex-start"
                                        secondaryAction={
                                            editId === note.customerNoteId ? (
                                                <Box>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="save"
                                                        onClick={() => void saveEdit()}
                                                        disabled={saving}
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="cancel"
                                                        onClick={cancelEdit}
                                                        disabled={saving}
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Box>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="edit"
                                                        onClick={() => startEdit(note)}
                                                        disabled={saving}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        onClick={() => setNoteIdPendingDelete(note.customerNoteId)}
                                                        disabled={saving}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            )
                                        }
                                    >
                                        {editId === note.customerNoteId ? (
                                            <Box sx={{ width: '100%', pr: 10 }}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    value={editText}
                                                    onChange={(e) => {
                                                        const v = filterNoteInput(e.target.value);
                                                        if (v.length <= NOTE_MAX) setEditText(v);
                                                    }}
                                                    size="small"
                                                    sx={{ mb: 1 }}
                                                />
                                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                                    <Box sx={remindDatePickerRowSx}>
                                                        <Box sx={remindDatePickerColSx}>
                                                            <DatePicker
                                                                label="Remind me on (optional)"
                                                                value={editReminder}
                                                                onChange={setEditReminder}
                                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </LocalizationProvider>
                                            </Box>
                                        ) : (
                                            <ListItemText
                                                sx={{ minWidth: 0 }}
                                                primaryTypographyProps={{
                                                    sx: {
                                                        whiteSpace: 'pre-wrap',
                                                        overflowWrap: 'break-word',
                                                        wordBreak: 'break-word',
                                                    },
                                                }}
                                                primary={note.text}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="caption" display="block">
                                                            {formatStamp(note.createdUtc)}
                                                            {note.notifyDateTimeUtc
                                                                ? ` · Reminder: ${formatReminderDate(note.notifyDateTimeUtc)}`
                                                                : ''}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        )}
                                    </ListItem>
                                    <Divider component="li" />
                                </React.Fragment>
                            ))
                        )}
                    </List>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="subtitle2" component="span">
                            Add note
                        </Typography>
                        <Tooltip title={NOTE_RULES_TOOLTIP} arrow placement="top">
                            <IconButton
                                size="small"
                                aria-label="Allowed characters and maximum length for notes"
                                sx={{ p: 0.25, color: 'primary.main' }}
                            >
                                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Note text"
                        value={newText}
                        onChange={(e) => {
                            const v = filterNoteInput(e.target.value);
                            if (v.length <= NOTE_MAX) setNewText(v);
                        }}
                        helperText={`${newText.length} / ${NOTE_MAX}`}
                        size="small"
                        sx={{ mb: 1 }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <Box sx={{ ...remindDatePickerRowSx, mb: 1 }}>
                            <Box sx={remindDatePickerColSx}>
                                <DatePicker
                                    label="Remind me on (optional)"
                                    value={newReminder}
                                    onChange={setNewReminder}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Box>
                        </Box>
                    </LocalizationProvider>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => void handleAdd()}
                        disabled={saving || !newText.trim()}
                    >
                        Add note
                    </Button>
                </>
            )}
            <ConfirmDialog
                open={noteIdPendingDelete !== null}
                title="Delete note"
                message="Are you sure you want to delete this note? This action cannot be undone."
                confirmLabel="Yes"
                cancelLabel="No"
                onClose={closeDeleteDialog}
                onConfirm={() => void confirmDeleteNote()}
            />
        </Box>
    );
};

export default CustomerNotesPanel;

declare global {
    interface Window {
        refreshNotifications?: () => void;
    }
}
